import { create } from 'zustand/react';
import { ImportPriceSuggestionPricesState } from './types';
import {
  getISO_8601Date,
  recognizedMgItemNrHeadings,
  recognizedSupplierIdHeadings,
  recognizedValidFromHeadings,
  recognizedValidToHeadings,
} from '@retail/utils';
import { getIsRecognizedHeaderPredicate, recognizedSalesPriceHeadings } from './utils';
import { AcceptedImportColumn } from '@retail/price-suggestion/types';

export const useImportPriceSuggestionPricesStore = create<ImportPriceSuggestionPricesState>(
  (set) => ({
    importedItemPrices: undefined,
    missingColumns: undefined,
    importedFile: undefined,

    importSpreadsheet: (rows, file) => {
      const [headerRow, ...dataRows] = rows;

      const mgItemNumberColumnIndex = headerRow.findIndex((header) =>
        recognizedMgItemNrHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
      );
      const supplierIdColumnIndex = headerRow.findIndex((header) =>
        recognizedSupplierIdHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
      );
      const salesPriceColumnIndex = headerRow.findIndex((header) =>
        recognizedSalesPriceHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
      );
      const validFromColumnIndex = headerRow.findIndex((header) =>
        recognizedValidFromHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
      );
      const validToColumnIndex = headerRow.findIndex((header) =>
        recognizedValidToHeadings.some(getIsRecognizedHeaderPredicate(`${header}`))
      );

      const missingColumns: AcceptedImportColumn[] = [];

      mgItemNumberColumnIndex === -1 && missingColumns.push('mgItem');
      supplierIdColumnIndex === -1 && missingColumns.push('supplierId');
      salesPriceColumnIndex === -1 && missingColumns.push('salesPrice');
      validFromColumnIndex === -1 && missingColumns.push('validFrom');

      const updatedState = dataRows.map((row) => {
        const mgItemNumber =
          mgItemNumberColumnIndex !== -1 ? Number(row.at(mgItemNumberColumnIndex)) : null;
        const mgSupplierId =
          supplierIdColumnIndex !== -1 ? Number(row.at(supplierIdColumnIndex)) : null;
        const price =
          salesPriceColumnIndex !== -1
            ? Number(`${row.at(salesPriceColumnIndex)}`?.replace(',', '.'))
            : null;
        const validFrom = validFromColumnIndex !== -1 ? row.at(validFromColumnIndex) + '' : null;
        const validTo = validToColumnIndex !== -1 ? row.at(validToColumnIndex) + '' : undefined;

        return {
          mgItemNumber,
          mgSupplierId,
          price,
          validFrom:
            validFrom && !isNaN(Date.parse(validFrom))
              ? getISO_8601Date(new Date(validFrom))
              : getISO_8601Date(new Date()),
          validTo:
            validTo && !isNaN(Date.parse(validTo)) ? getISO_8601Date(new Date(validTo)) : undefined,
        };
      });

      set({
        importedItemPrices: missingColumns.length > 0 ? undefined : updatedState,
        missingColumns,
        importedFile: file,
      });
    },

    clearState: () =>
      set({ importedItemPrices: undefined, missingColumns: undefined, importedFile: undefined }),
  })
);
